import React, { useState, useEffect } from 'react';

export default function Sidebar({ items = [], activePath = '', domain = 'ops', masterUrl = '/', onOpenKi, mobileOpen = false, onMobileClose }) {
  const accent = `var(--accent-${domain})`;
  const [collapsed, setCollapsed] = useState({});

  const toggle = (label) => {
    setCollapsed(prev => ({ ...prev, [label]: !prev[label] }));
  };

  // Close sidebar on route change (mobile)
  useEffect(() => {
    if (mobileOpen && onMobileClose) onMobileClose();
  }, [activePath]);

  // Lock body scroll when mobile sidebar is open
  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = 'hidden';
      return () => { document.body.style.overflow = ''; };
    }
  }, [mobileOpen]);

  return (
    <>
      {/* Overlay for mobile */}
      <div
        className={`sidebar-overlay${mobileOpen ? ' open' : ''}`}
        onClick={onMobileClose}
      />

      <nav className={`eco-sidebar${mobileOpen ? ' eco-sidebar--open' : ''}`}>
        {/* Mobile close header */}
        <div className="eco-sidebar__mobile-header">
          <span style={{ fontWeight: 700, color: accent, textTransform: 'uppercase', fontSize: 'var(--font-size-sm)' }}>
            {domain}
          </span>
          <button
            onClick={onMobileClose}
            style={{
              background: 'none',
              border: 'none',
              color: 'var(--text-muted)',
              cursor: 'pointer',
              fontSize: 20,
              padding: '4px 8px',
              minHeight: 44,
              minWidth: 44,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            &times;
          </button>
        </div>

        <div style={{ flex: 1, padding: 'var(--gap-sm) 0', overflowY: 'auto' }}>
          {items.map((section, si) => (
            <div key={si} style={{ marginBottom: 'var(--gap-xs)' }}>
              {/* Section header */}
              <div
                onClick={() => section.children && toggle(section.label)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '6px var(--gap-md)',
                  cursor: section.children ? 'pointer' : 'default',
                  fontSize: 'var(--font-size-xs)',
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  color: 'var(--text-muted)',
                  userSelect: 'none',
                  minHeight: 32,
                }}
              >
                <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  {section.icon && <span>{section.icon}</span>}
                  {section.label}
                </span>
                {section.children && (
                  <span style={{ fontSize: 10, transition: 'transform var(--transition-fast)', transform: collapsed[section.label] ? 'rotate(-90deg)' : 'rotate(0)' }}>
                    &#9660;
                  </span>
                )}
              </div>

              {/* Sub-items */}
              {section.children && !collapsed[section.label] && (
                <div>
                  {section.children.map((item, ii) => {
                    const isExternal = item.path?.startsWith('http');
                    const isActive = !isExternal && activePath === item.path;
                    const isPlanned = item.badge === 'geplant';
                    return (
                      <a
                        key={ii}
                        href={isPlanned ? undefined : item.path}
                        target={isExternal ? '_blank' : undefined}
                        rel={isExternal ? 'noopener noreferrer' : undefined}
                        onClick={e => {
                          if (isPlanned) { e.preventDefault(); return; }
                          if (!isExternal && item.onClick) {
                            e.preventDefault();
                            item.onClick(item.path);
                          }
                        }}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          padding: '8px var(--gap-md) 8px var(--gap-lg)',
                          minHeight: 36,
                          fontSize: 'var(--font-size-sm)',
                          color: isPlanned ? 'var(--text-muted)' : isActive ? accent : 'var(--text-secondary)',
                          opacity: isPlanned ? 0.5 : 1,
                          cursor: isPlanned ? 'default' : 'pointer',
                          background: isActive ? 'var(--bg-tertiary)' : 'transparent',
                          borderLeft: isActive ? `2px solid ${accent}` : '2px solid transparent',
                          textDecoration: 'none',
                          transition: 'all var(--transition-fast)',
                        }}
                      >
                        <span className="truncate">{item.label}</span>
                        {item.badge && (
                          <span style={{
                            fontSize: 'var(--font-size-xs)',
                            background: 'var(--bg-tertiary)',
                            padding: '1px 6px',
                            borderRadius: 8,
                            color: 'var(--text-muted)',
                          }}>
                            {item.badge}
                          </span>
                        )}
                      </a>
                    );
                  })}
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Bottom links */}
        <div style={{ borderTop: '1px solid var(--border)', padding: 'var(--gap-sm) var(--gap-md)', display: 'flex', flexDirection: 'column', gap: 4 }}>
          <a
            href={masterUrl}
            style={{
              display: 'flex',
              alignItems: 'center',
              minHeight: 36,
              fontSize: 'var(--font-size-xs)',
              color: 'var(--text-muted)',
              padding: '4px 0',
            }}
          >
            &#8962; Master
          </a>
        </div>
      </nav>
    </>
  );
}
